/**
 * The meters: run energy, and health.
 *
 * A ring that empties as the thing behind it runs out, with a figure in the
 * middle saying which thing it is. They sit by the interface buttons where a
 * thumb and an eye both already are, and either can be dragged anywhere else:
 * these are the two pieces of the interface somebody watches while they are
 * moving rather than while they are stopped, and where that belongs depends on
 * the screen.
 *
 * Clicking the run meter is the same as pressing R. The health meter is a
 * reading rather than a control, so it does nothing when pressed.
 */

import { useCallback, useState } from 'react';
import type { ContextMenu, MenuEntry } from '../game/menu';
import { useAltHeld } from './alt-mode';
import { useWindowGrips } from './grips';
import {
  loadLook,
  readingOf,
  saveLook,
  type MeterIcon,
  type MeterLook,
  type MeterShape,
  type MeterValue,
} from './meter-look';
import { useDraggableWindow } from './draggable';

const SHAPES: { shape: MeterShape; label: string }[] = [
  { shape: 'circle', label: 'Circle' },
  { shape: 'horizontal', label: 'Horizontal' },
  { shape: 'vertical', label: 'Vertical' },
];

const VALUES: { value: MeterValue; label: string }[] = [
  { value: 'current', label: 'Current' },
  { value: 'both', label: 'Current and max' },
  { value: 'percent', label: 'Percentage' },
];

/**
 * The choices behind a gauge, as entries for the one right click menu.
 *
 * Only the ones that mean something for the shape it is in: a ring has no side
 * for its icon to sit on, and a bar has no middle to put a large number in. A
 * menu that lists choices which do nothing is a menu somebody stops reading.
 */
function lookEntries(look: MeterLook, change: (look: MeterLook) => void): MenuEntry[] {
  const heading = (label: string): MenuEntry => ({ label, heading: true, run: () => undefined });

  // Icon positions are named for the shape they are in: the same choice reads
  // as left and right lying down and top and bottom standing up.
  const places: { icon: MeterIcon; label: string }[] =
    look.shape === 'vertical'
      ? [
          { icon: 'first', label: 'Above' },
          { icon: 'second', label: 'Below' },
          { icon: 'hidden', label: 'Hidden' },
        ]
      : [
          { icon: 'first', label: 'Left' },
          { icon: 'second', label: 'Right' },
          { icon: 'hidden', label: 'Hidden' },
        ];

  return [
    heading('Shape'),
    ...SHAPES.map((one) => ({
      label: one.label,
      checked: look.shape === one.shape,
      run: () => change({ ...look, shape: one.shape }),
    })),
    heading('Value'),
    ...VALUES.map((one) => ({
      label: one.label,
      checked: look.value === one.value,
      run: () => change({ ...look, value: one.value }),
    })),
    ...(look.shape === 'circle'
      ? [
          heading('Middle'),
          {
            label: 'Large text',
            checked: look.bigText,
            run: () => change({ ...look, bigText: !look.bigText }),
          },
        ]
      : [
          heading('Icon'),
          ...places.map((one) => ({
            label: one.label,
            checked: look.icon === one.icon,
            run: () => change({ ...look, icon: one.icon }),
          })),
        ]),
  ];
}

/** The smallest a bar may be pulled to, lying down and standing up. */
const LEAST_BAR = {
  horizontal: { width: 56, height: 14 },
  vertical: { width: 14, height: 56 },
};

/** The ring, drawn as a circle whose dash is the part that is filled. */
const RADIUS = 17;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/**
 * The gauge itself, which is the same whatever it is measuring.
 *
 * Kept apart from what it means, so a second gauge is a different middle and a
 * different colour rather than a second copy of the dragging, the storing, the
 * drawing and the three shapes it can be drawn in.
 */
function Meter({
  kind,
  name,
  part,
  label,
  colour,
  lit,
  current,
  max,
  children,
  onPress,
  onMenu,
  away,
}: {
  /** Which gauge this is, for remembering where it was put and how it looks. */
  kind: string;
  /** What it measures, as the title of its menu. */
  name: string;
  part: number;
  label: string;
  colour: string;
  lit: boolean;
  current: number;
  max: number;
  /** What goes in the middle: a runner, a heart, a drop. */
  children: JSX.Element;
  onPress?: () => void;
  /** Opens its choices in the one menu everything else uses. */
  onMenu: (menu: ContextMenu) => void;
  /** How far to the left of the interface buttons it starts, in pixels. */
  away: number;
}): JSX.Element {
  const home = useCallback(
    (size: { width: number; height: number }) => {
      const strip = document.querySelector('.tabs')?.getBoundingClientRect();
      const right = strip ? window.innerWidth - strip.left : 96;

      return {
        x: window.innerWidth - size.width - right - away,
        y: window.innerHeight - size.height - 14,
      };
    },
    [away],
  );

  const { attach, style, onPointerDown, wasDragged, nudge } = useDraggableWindow(kind, {
    home,
    grabAnywhere: true,
  });

  const [look, setLook] = useState<MeterLook>(() => loadLook(kind));

  /*
   * A bar can be pulled to any length while the interface is being arranged,
   * the same as a window. The ring keeps its size: it is a dial, and a dial
   * three times the size is not three times as readable.
   *
   * Kept per shape, so going from a long bar to a ring and back brings the long
   * bar back.
   */
  const altHeld = useAltHeld();
  const bar = look.shape !== 'circle';
  const { size, grips } = useWindowGrips(
    `${kind}.${look.shape}`,
    nudge,
    altHeld && bar,
    look.shape === 'vertical' ? LEAST_BAR.vertical : LEAST_BAR.horizontal,
  );
  const sized = bar && size !== null;

  const change = useCallback(
    (held: MeterLook) => {
      setLook(held);
      saveLook(kind, held);
    },
    [kind],
  );

  const reading = readingOf(look, current, max);
  const icon = (
    <svg viewBox="0 0 40 40" aria-hidden="true">
      <g className="meter-figure">{children}</g>
    </svg>
  );

  /** What is drawn, which is one of three quite different things. */
  const drawn =
    look.shape === 'circle' ? (
      <>
        <svg viewBox="0 0 40 40" aria-hidden="true">
          <circle className="meter-track" cx="20" cy="20" r={RADIUS} />
          <circle
            className="meter-fill"
            cx="20"
            cy="20"
            r={RADIUS}
            stroke={colour}
            strokeDasharray={`${CIRCUMFERENCE * part} ${CIRCUMFERENCE}`}
          />
          {/* The number in the middle instead of the figure, for anybody who
              would rather read it than judge it by how full the ring is. */}
          {look.bigText ? (
            <text
              className="meter-big"
              x="20"
              y="20"
              textAnchor="middle"
              dominantBaseline="central"
            >
              {reading}
            </text>
          ) : (
            <g className="meter-figure">{children}</g>
          )}
        </svg>

        {look.bigText ? null : <span className="meter-count">{reading}</span>}
      </>
    ) : (
      <>
        {look.icon === 'first' ? <span className="meter-icon">{icon}</span> : null}

        <span className="meter-bar">
          {/* Filled from the left lying down and from the bottom standing up,
              which is the way each of them is read. */}
          <span
            className="meter-bar-fill"
            style={{
              background: colour,
              ...(look.shape === 'vertical'
                ? { height: `${part * 100}%` }
                : { width: `${part * 100}%` }),
            }}
          />
          <span className="meter-bar-said">
            {look.shape === 'vertical' && look.value === 'both' ? (
              <span className="meter-fraction">
                <span>{current}</span>
                <span>{max}</span>
              </span>
            ) : (
              reading
            )}
          </span>
        </span>

        {look.icon === 'second' ? <span className="meter-icon">{icon}</span> : null}
      </>
    );

  return (
    <div
      className={`meter ${kind} shape-${look.shape}${lit ? ' on' : ''}${look.bigText ? ' big' : ''}${sized ? ' sized' : ''}${altHeld && bar ? ' ui-mode' : ''}`}
      ref={attach}
      style={{ ...style, ...(sized && size ? size : {}) }}
      onPointerDown={onPointerDown}
    >
      <button
        type="button"
        title={label}
        aria-label={label}
        onClick={() => {
          // The whole thing is the control and the handle both, so a press that
          // moved it is a move rather than a press.
          if (wasDragged()) return;
          onPress?.();
        }}
        onContextMenu={(event) => {
          event.preventDefault();
          onMenu({
            screenX: event.clientX,
            screenY: event.clientY,
            title: name,
            entries: lookEntries(look, change),
          });
        }}
      >
        {drawn}
      </button>

      {grips}
    </div>
  );
}

export function RunMeter({
  energy,
  max,
  running,
  onToggle,
  onMenu,
}: {
  energy: number;
  max: number;
  running: boolean;
  onToggle: () => void;
  onMenu: (menu: ContextMenu) => void;
}): JSX.Element {
  const part = max > 0 ? Math.max(0, Math.min(1, energy / max)) : 0;
  const left = Math.round(part * 100);

  /*
   * One colour per gauge, whatever it is reading.
   *
   * These used to change hue as they emptied, which meant the colour said how
   * full a gauge was and nothing said which gauge it was. Four of them side by
   * side and all amber is four things nobody can tell apart at a glance. Running
   * low is said by lighting the gauge instead, which is a different channel and
   * leaves the colour free to mean stamina.
   */
  const colour = 'var(--gauge-stamina)';

  return (
    <Meter
      kind="run-meter"
      name="Stamina"
      onMenu={onMenu}
      part={part}
      colour={colour}
      lit={running}
      current={left}
      max={100}
      away={12}
      label={`${running ? 'Running' : 'Walking'}, ${left}% left. Click or press R.`}
      onPress={onToggle}
    >
      {/* A runner: head, body, two legs and two arms, mid stride. Drawn rather
          than written so it reads the same wherever the game is opened.

          Every limb is its own path, hung off the shoulder or the hip it belongs
          to. Written as one long polyline instead, the arms came out as a single
          limb with two elbows in it and the other arm was simply missing: the
          line ran on from the end of the first rather than starting again. */}
      <>
        <circle cx="23" cy="11.5" r="2.4" />

        {/* Leaning into it, which is most of what says running rather than
            standing. */}
        <path d="M22 14.5 18.5 21" />

        <path d="M18.5 21 22 24l1 4.5" />
        <path d="M18.5 21 14.5 25.5" />

        <path d="M20.5 17 24.5 18.5l1.5 3.5" />
        <path d="M20.5 17 16.5 15.5l-2 2.5" />
      </>
    </Meter>
  );
}

/** Health, in the same ring, with a heart in it. */
export function HealthMeter({
  health,
  max,
  onMenu,
}: {
  health: number;
  max: number;
  onMenu: (menu: ContextMenu) => void;
}): JSX.Element {
  const part = max > 0 ? Math.max(0, Math.min(1, health / max)) : 0;

  const colour = 'var(--gauge-health)';

  return (
    <Meter
      kind="health-meter"
      name="Health"
      onMenu={onMenu}
      part={part}
      colour={colour}
      lit={part <= 0.25}
      current={health}
      max={max}
      away={74}
      label={`${health} of ${max} health`}
    >
      {/* A heart, drawn as two curves meeting at a point. */}
      <path
        className="meter-solid"
        d="M20 27.5c-5-3.4-8-6.3-8-9.7a4.2 4.2 0 0 1 8-1.8 4.2 4.2 0 0 1 8 1.8c0 3.4-3 6.3-8 9.7Z"
      />
    </Meter>
  );
}

/** Mana, in the same gauge, with a drop in it. */
export function ManaMeter({
  mana,
  max,
  onMenu,
}: {
  mana: number;
  max: number;
  onMenu: (menu: ContextMenu) => void;
}): JSX.Element {
  const part = max > 0 ? Math.max(0, Math.min(1, mana / max)) : 0;

  return (
    <Meter
      kind="mana-meter"
      name="Mana"
      onMenu={onMenu}
      part={part}
      colour="var(--gauge-mana)"
      lit={part <= 0.25}
      current={mana}
      max={max}
      away={136}
      label={`${mana} of ${max} mana`}
    >
      {/* A drop: a point at the top and a bowl under it. */}
      <path
        className="meter-solid"
        d="M20 11c4.2 5 6.5 8.2 6.5 10.9a6.5 6.5 0 0 1-13 0C13.5 19.2 15.8 16 20 11Z"
      />
    </Meter>
  );
}

/** Faith, in the same gauge, with a rising sun in it. */
export function FaithMeter({
  faith,
  max,
  onMenu,
}: {
  faith: number;
  max: number;
  onMenu: (menu: ContextMenu) => void;
}): JSX.Element {
  const part = max > 0 ? Math.max(0, Math.min(1, faith / max)) : 0;
  const shown = Math.ceil(faith);

  return (
    <Meter
      kind="faith-meter"
      name="Faith"
      onMenu={onMenu}
      part={part}
      colour="var(--gauge-faith)"
      lit={part <= 0.25}
      current={shown}
      max={max}
      away={198}
      label={`${shown} of ${max} faith`}
    >
      {/* A sun coming up over a line of ground. */}
      <>
        <path d="M11 25h18M14.5 25a5.5 5.5 0 0 1 11 0" />
        <path d="M20 13v3M13 16.5l2 2M27 16.5l-2 2" />
      </>
    </Meter>
  );
}
